import { useLazyQuery, useMutation } from '@apollo/client';
import { CREATE_ADDRESS, CREATE_POSTAL_CODE, GET_ADDRESS_BUSINESS_ENTITIES, SEARCH_ADDRESSES, SEARCH_POSTAL_CODES, UPDATE_ADDRESS } from '../../../graphql/address-gql';
import { FetchPolicyOption } from '../../../services/apollo/apollo';
import { IAddress, IAddressInput, IAddressUsagesResponse, ICreateAddressResponse, ISearchAddressesInput, ISearchAddressesResponse, IUpdateAddressResponse } from '../../../types/address';
import { NamedEntity } from '../../../types/common';
import { Action } from '../../../types/reducer-action';
import { parseObject, trimToEmpty } from '../../../utils/commons';
import { ApolloErrorAction, DataEntryEvent } from '../../common/data-entry-reducer';
import { AddressForm, AddressManagerDomain, useAddressDispatch } from './address-manager-context';

export const isSameAddress = (a?: IAddress, b?: IAddress) => {
  if (!a && !b) return true;
  if (!a || !b) return false;

  const left = { ...a } as Record<string, any>;
  const right = { ...b } as Record<string, any>;

  const keys = [
    'buildingName',
    'civicNumber',
    'civicNumberSuffix',
    'streetName',
    'streetTypeCodeId',
    'streetDirCodeId',
    'municipalityId',
    'provinceId',
    'countryId',
    'postalCodeId',
    'deliveryTypeCodeId',
    'poBoxNumber',
    'installationTypeCodeId',
    'installationName',
    'routeServiceTypeCodeId',
    'routeServiceNumber',
    'siteNumber',
    'compartmentNumber'
  ];

  return keys.every(key => trimToEmpty(right[key]).toLowerCase() === trimToEmpty(left[key]).toLowerCase());
};

interface AddressApi {
  searchAddresses: (input: ISearchAddressesInput) => void;
  createAddress: (form: AddressForm) => Promise<boolean>;
  updateAddress: (id: number, form: AddressForm) => Promise<boolean>;
  getAssociatedBusinessEntities: (addressId: number) => void;
  busy: boolean;
}

export default function useAddressApi(): AddressApi {
  const dispatch = useAddressDispatch();

  const [search, { loading: searching }] = useLazyQuery<ISearchAddressesResponse>(SEARCH_ADDRESSES, FetchPolicyOption.NetworkOnly);

  const [create, { loading: creating }] = useMutation<ICreateAddressResponse>(CREATE_ADDRESS);

  const [update, { loading: updating }] = useMutation<IUpdateAddressResponse>(UPDATE_ADDRESS);

  const [getAddressUsages, { loading: gettingUsages }] = useLazyQuery<IAddressUsagesResponse>(GET_ADDRESS_BUSINESS_ENTITIES);

  const [createPostalCode, { loading: creatingPostalCode }] = useMutation<{ createPostalCode: NamedEntity }>(CREATE_POSTAL_CODE);

  const setResults = (results: IAddress[] | undefined) =>
    dispatch(new Action(DataEntryEvent.NewDataSet, results));

  const searchAddresses = (input: ISearchAddressesInput) => {
    setResults(undefined);

    search({ variables: { input } })
      .then(response => setResults(response.data?.searchAddresses));
  };

  const createAddress = async (form: AddressForm) => {
    const input = await toAddressInput(form);

    return !!input && _checkDuplication(form)
      .then(() =>
        create({
          variables: {
            input: input
          },
          onError: (error) => dispatch(new ApolloErrorAction(error))
        }).then(response => !!response.data?.createAddress));
  };

  const updateAddress = async (id: number, form: AddressForm) => {
    const input = await toAddressInput(form);
    if (!input) return false;

    // remove streetDirFrench,streetTypeFrench,deliveryTypeFrench,installationTypeFrench,routeServiceTypeFrench,generalDeliveryFrench
    const newInput = { ...input } as Record<string, any>;
    Object.keys(newInput)
      .filter(key => ['streetDirFrench', 'streetTypeFrench', 'deliveryTypeFrench', 'installationTypeFrench', 'routeServiceTypeFrench', 'generalDeliveryFrench'].includes(key))
      .forEach(key => delete newInput[key]);

    return _checkDuplication(form)
      .then(() =>
        update({
          variables: {
            id: id,
            input: newInput
          },
          onError: (error) => dispatch(new ApolloErrorAction(error))
        }).then(response => {
          const result = response.data?.updateAddress;
          dispatch(new Action(DataEntryEvent.UpdateData, result));
          return !!result;
        })
      );
  };

  const getAssociatedBusinessEntities = (addressId: number) => {
    getAddressUsages({
      variables: {
        addressId: addressId
      }
    }).then(response =>
      dispatch(new Action(AddressManagerDomain.BusinessEntities + DataEntryEvent.NewDataSet,
        response.data?.addressBusinessEntities
      ))
    );
  };

  async function _checkDuplication(form: AddressForm) {
    const input = {
      address: form.streetName ?? form.poBoxNumber ?? form.routeServiceNumber,
      municipalityId: form.municipalityId,
      postalCodeId: form.postalCodeId
    } as ISearchAddressesInput;

    const response = await search({
      variables: { input }
    });
    const results = response.data?.searchAddresses;
    if (!!results && results.some(address => address.id !== form.id && isSameAddress(address, form))) {
      throw new Error('This address already exists and cannot be duplicated.');
    }
  }

  async function _createPostalCode(postalCode?: string) {
    return createPostalCode({
      variables: {
        postalCode: postalCode
      },
      onError: (error) => dispatch(new ApolloErrorAction(error)),
      update: (cache, { data }) => {
        const createResult = data?.createPostalCode;
        if (!createResult) return;

        const results = cache.readQuery<{ postalCodes: NamedEntity[] }>({
          query: SEARCH_POSTAL_CODES
        })?.postalCodes ?? [];

        const newResults = [createResult, ...results];

        cache.writeQuery({
          query: SEARCH_POSTAL_CODES,
          data: { postalCodes: newResults }
        });
      }
    }).then(response => response.data?.createPostalCode);
  }

  async function toAddressInput(form?: AddressForm) {
    if (!form) return undefined;

    const { id, postalCode, ...input } = form;

    let postalCodeId = form.postalCodeId;
    if (!postalCodeId) {
      const pc = await _createPostalCode(postalCode);
      if (!pc) {
        return undefined;
      }
      postalCodeId = pc.id;
    }

    input.postalCodeId = postalCodeId;
    input.expired = form.expired?.toString() === 'true';
    input.buildingNameFrenchSync = form.buildingNameFrenchSync ? 1 : 0;

    return parseObject<IAddressInput>(input);
  }

  const busy = searching || creating || updating || gettingUsages || creatingPostalCode;

  return {
    searchAddresses,
    createAddress,
    updateAddress,
    getAssociatedBusinessEntities,
    busy
  };
}
